import React, { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import axios from "axios";
import {
  Monitor, Cpu, HardDrive, Headphones, Users, Globe, Menu, X, 
  MessageCircle, MessageSquare, Send, Phone, Mail, MapPin, ChevronRight, 
  Ticket, LayoutDashboard, LogOut, Plus, Clock, CheckCircle,
  AlertCircle, User, Settings, Loader2, ArrowRight, Download,
  MousePointer, Shield, Wifi, Printer, Upload, FileText,
  Search, Copy, ExternalLink, Sparkles, Target, TrendingUp,
  Share2, Link2, Zap, BarChart3, Hash, Image as ImageIcon, Trash2,
  Laptop, HandCoins
} from "lucide-react";
import "./index.css";

// SEO Component
import SEO, { pageSEO } from "./components/SEO";

// Import Page Components
import PCBuilderPage from "./pages/PCBuilderPage";
import DiagnosticPage from "./pages/DiagnosticPage";
import PromoteYourSitePage from "./pages/PromoteYourSitePage";
import RemoteSupportPage from "./pages/RemoteSupportPage";
import PrintingServicesPage from "./pages/PrintingServicesPage";
import WebDevelopmentPage from "./pages/WebDevelopmentPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage"; // FROZEN PAGE
import TutorialsPage from "./pages/TutorialsPage";
import AboutSmartITPage from "./pages/AboutSmartITPage";
import ReviewsPage from "./pages/ReviewsPage";
import SellDevicePage from "./pages/SellDevicePage";
import AuthPage from "./pages/AuthPage";
import AssistProPage from "./pages/AssistProPage";

// ScrollToTop Component - scrolls to top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Shared constants
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "447435324947";

// Configure axios to include credentials (cookies) with all requests
axios.defaults.withCredentials = true;

// Auth Context (exported for modular components)
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Setup axios interceptor for token refresh
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);
const onRefreshed = () => {
  refreshSubscribers.forEach(cb => cb());
  refreshSubscribers = [];
};

// Axios response interceptor for automatic token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Don't retry for refresh endpoint itself, logout, or login
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/refresh') || 
                           originalRequest?.url?.includes('/auth/logout') ||
                           originalRequest?.url?.includes('/auth/login');
    
    // If 401 and not already retrying and not an auth endpoint
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Wait for refresh to complete
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(() => {
            resolve(axios(originalRequest));
          });
          // Add timeout to prevent infinite wait
          setTimeout(() => {
            reject(error);
          }, 5000);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Try to refresh the token
        await axios.post(`${API}/auth/refresh`);
        isRefreshing = false;
        onRefreshed();
        // Retry original request
        return axios(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        // Refresh failed - user needs to login again
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check session on mount (cookies sent automatically)
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
      } catch (err) {
        // Session invalid or expired - user is not logged in
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  const login = async (email) => {
    const res = await axios.post(`${API}/auth/login`, { email });
    // Cookies are automatically set by the browser from the Set-Cookie header
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, phone) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, phone });
    // Cookies are automatically set by the browser from the Set-Cookie header
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Ignore errors - cookies will be cleared by the server
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" />;
  
  return children;
};

// Loading Screen
const LoadingScreen = () => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center">
    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
  </div>
);

// Navbar Component - Modern Design
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // Close menu first
    setIsOpen(false);
    setServicesOpen(false);
    
    const currentPath = window.location.pathname;
    
    // Small delay to allow menu animation to complete
    setTimeout(() => {
      if (currentPath !== "/") {
        navigate("/");
        setTimeout(() => {
          const section = document.getElementById(sectionId);
          if (section) {
            section.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      } else {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }, 150);
  };

  const servicesDropdownItems = [
    { name: "Remote Support", path: "/remote-support", icon: Headphones },
    { name: "Diagnostic", path: "/diagnostic", icon: Monitor },
    { name: "PC Builder", path: "/pc-builder", icon: Cpu },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.services-dropdown')) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-slate-950/98 backdrop-blur-xl border-b border-slate-800/50' 
        : 'bg-slate-950/95 backdrop-blur-lg'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[75px]">
          {/* Logo - Left - Clicks navigates home and scrolls to top */}
          <button 
            onClick={() => {
              navigate('/');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-2 flex-shrink-0 cursor-pointer"
            data-testid="logo-home-link"
          >
            <span className="text-2xl font-bold text-white font-['Poppins']">
              Smart<span className="text-blue-500">IT</span>
            </span>
          </button>

          {/* Desktop Menu - Center */}
          <div className="hidden lg:flex items-center justify-center flex-1 gap-[32px]">
            {/* Services - Scroll to section */}
            <button 
              onClick={() => scrollToSection("services")}
              className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap"
              data-testid="home-services-btn"
            >
              Services
            </button>
            <Link to="/printing-services" className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap" data-testid="nav-printing-btn">
              Printing Services
            </Link>
            <Link to="/assist-pro" className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap" data-testid="nav-assist-pro-btn">
              Assist Pro
            </Link>
            <Link to="/tutorials" className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap" data-testid="nav-tutorials-btn">
              Tutorials
            </Link>
            <Link to="/reviews" className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap" data-testid="nav-reviews-btn">
              Reviews
            </Link>
            <Link to="/about-smartit" className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap" data-testid="nav-about-btn">
              About SmartIT
            </Link>
            <button onClick={() => scrollToSection("contact")} className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap">
              Contact
            </button>
          </div>

          {/* Open a Ticket Button - Right */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-3">
                <Link 
                  to={user.role === "admin" ? "/admin" : "/dashboard"} 
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold text-[15px] py-2.5 px-5 rounded-[7px] transition-all font-['Inter'] shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30"
                  data-testid="nav-customer-portal"
                >
                  {user.role === "admin" ? "Admin Panel" : "Open a Ticket"}
                </Link>
                <button 
                  onClick={logout} 
                  className="flex items-center gap-1.5 text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter']"
                  data-testid="nav-logout"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            ) : (
              <Link 
                to="/dashboard" 
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold text-[15px] py-2.5 px-5 rounded-[7px] transition-all font-['Inter'] shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30"
                data-testid="nav-customer-portal"
              >
                Open a Ticket
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors" 
            onClick={() => setIsOpen(!isOpen)} 
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-slate-900/95 backdrop-blur-xl border-t border-slate-800/50"
          >
            <div className="px-4 py-4 space-y-1">
              {/* Services - Scroll to section */}
              <button 
                onClick={() => scrollToSection("services")}
                className="block w-full text-left py-3 px-3 text-white/90 hover:text-[#4da3ff] hover:bg-white/5 rounded-lg transition-all text-[15px] font-semibold font-['Inter']"
              >
                Services
              </button>
              <Link 
                to="/printing-services" 
                onClick={() => setIsOpen(false)}
                className="block w-full text-left py-3 px-3 text-white/90 hover:text-[#4da3ff] hover:bg-white/5 rounded-lg transition-all text-[15px] font-semibold font-['Inter']"
                data-testid="mobile-printing-btn"
              >
                Printing Services
              </Link>
              <Link 
                to="/assist-pro" 
                onClick={() => setIsOpen(false)}
                className="block w-full text-left py-3 px-3 text-white/90 hover:text-[#4da3ff] hover:bg-white/5 rounded-lg transition-all text-[15px] font-semibold font-['Inter']"
                data-testid="mobile-assist-pro-btn"
              >
                Assist Pro
              </Link>
              <Link 
                to="/tutorials" 
                onClick={() => setIsOpen(false)}
                className="block w-full text-left py-3 px-3 text-white/90 hover:text-[#4da3ff] hover:bg-white/5 rounded-lg transition-all text-[15px] font-semibold font-['Inter']"
                data-testid="mobile-tutorials-btn"
              >
                Tutorials
              </Link>
              <Link 
                to="/reviews" 
                onClick={() => setIsOpen(false)}
                className="block w-full text-left py-3 px-3 text-white/90 hover:text-[#4da3ff] hover:bg-white/5 rounded-lg transition-all text-[15px] font-semibold font-['Inter']"
                data-testid="mobile-reviews-btn"
              >
                Reviews
              </Link>
              <Link 
                to="/about-smartit" 
                onClick={() => setIsOpen(false)}
                className="block w-full text-left py-3 px-3 text-white/90 hover:text-[#4da3ff] hover:bg-white/5 rounded-lg transition-all text-[15px] font-semibold font-['Inter']"
                data-testid="mobile-about-btn"
              >
                About SmartIT
              </Link>
              <button onClick={() => scrollToSection("contact")} className="block w-full text-left py-3 px-3 text-white/90 hover:text-[#4da3ff] hover:bg-white/5 rounded-lg transition-all text-[15px] font-semibold font-['Inter']">
                Contact
              </button>
              
              {/* Divider */}
              <div className="border-t border-slate-700/50 my-2"></div>
              
              {user ? (
                <>
                  <Link 
                    to={user.role === "admin" ? "/admin" : "/dashboard"} 
                    className="block text-center bg-blue-500 hover:bg-blue-600 text-white font-semibold text-[15px] py-3 px-5 rounded-[7px] transition-all font-['Inter']" 
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-customer-portal"
                  >
                    {user.role === "admin" ? "Admin Panel" : "Open a Ticket"}
                  </Link>
                  <button onClick={() => { logout(); setIsOpen(false); }} className="block w-full text-left py-3 px-3 text-white/90 hover:text-[#4da3ff] hover:bg-white/5 rounded-lg transition-all text-[15px] font-semibold font-['Inter']">
                    <span className="flex items-center gap-2"><LogOut className="w-4 h-4" /> Logout</span>
                  </button>
                </>
              ) : (
                <Link 
                  to="/dashboard" 
                  className="block text-center bg-blue-500 hover:bg-blue-600 text-white font-semibold text-[15px] py-3 px-5 rounded-[7px] transition-all font-['Inter'] mt-3" 
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-customer-portal"
                >
                  Open a Ticket
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-[60vh] flex items-center justify-center bg-slate-950 overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-slate-950 to-cyan-900/10" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent" />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Professional IT Services
            <br />
            <span className="text-gradient">in Aylesbury</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            Fast, reliable and friendly support for home users and small businesses.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToServices}
              className="bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3.5 px-8 rounded-xl transition-all duration-300 shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 flex items-center justify-center gap-2"
              data-testid="hero-get-support-btn"
            >
              Get Support
              <ChevronRight className="w-5 h-5" />
            </button>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I need IT support`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-500 text-white font-semibold py-3.5 px-8 rounded-xl transition-all duration-300 shadow-lg shadow-green-600/25 hover:shadow-green-500/40 flex items-center justify-center gap-2"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Contact on WhatsApp
            </a>
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-950 to-transparent" />
    </section>
  );
};

// Volunteer Card - Compact
const VolunteerCard = () => (
  <div className="bg-slate-950 py-4">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-center gap-3 text-center">
        <span className="text-xl">🐾</span>
        <p className="text-slate-400 text-base">
          <span className="text-slate-300 font-medium">Community Volunteer</span>
          <span className="mx-2 text-slate-600">•</span>
          I volunteer in my local community, including support activities for the RSPCA.
        </p>
      </div>
    </div>
  </div>
);

// IT Services Data
const itServices = [
  {
    icon: Monitor,
    title: "Device Diagnostic",
    description: "Comprehensive testing and analysis to identify issues with your computer, laptop or device.",
    link: "/diagnostic",
  },
  {
    icon: Settings,
    title: "Repairs & Maintenance",
    description: "Expert repairs for hardware and software issues. Get your device running smoothly again.",
    link: null,
  },
  {
    icon: Cpu,
    title: "Hardware Upgrades",
    description: "Boost performance with SSD, RAM and component upgrades for faster, more efficient systems.",
    link: null,
  },
  {
    icon: HardDrive,
    title: "Data Recovery & Backup",
    description: "Recover lost files and set up reliable backup solutions to protect your valuable data.",
    link: null,
  },
  {
    icon: Headphones,
    title: "Remote Support",
    description: "Quick online support to resolve software issues without leaving your home or office.",
    link: "/remote-support",
  },
  {
    icon: Printer,
    title: "Printing Services",
    description: "Professional document printing with quick turnaround. Upload files and collect locally.",
    link: "/printing-services",
  },
];

// Digital & Business Solutions Data
const digitalServices = [
  {
    icon: Globe,
    title: "Website Building",
    description: "Custom websites designed to showcase your business and attract more customers online.",
    link: "/web-development",
    comingSoon: false,
  },
  {
    icon: Users,
    title: "IT Consulting",
    description: "Strategic IT advice to help your business make smart technology decisions.",
    link: null,
    comingSoon: true,
  },
  {
    icon: Cpu,
    title: "AI PC Builder",
    description: "Get personalized PC build recommendations with our AI-powered configurator tool.",
    link: "/pc-builder",
    comingSoon: true,
  },
  {
    icon: TrendingUp,
    title: "Smart Rank (SEO)",
    description: "Improve your online visibility and attract more customers with our SEO services.",
    link: "/promote-your-site",
    comingSoon: true,
  },
];

// IT Services Section
const ServicesSection = () => (
  <section id="services" className="py-20 bg-slate-950">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <p className="text-blue-500 font-semibold tracking-wide uppercase text-sm mb-3">What We Offer</p>
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">IT Services</h2>
        <p className="text-slate-400 max-w-2xl mx-auto">
          Technical support, repairs and diagnostics for all your devices.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {itServices.map((service, index) => (
          <motion.div
            key={service.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="bg-slate-900/60 border border-slate-800 hover:border-blue-500/30 rounded-xl p-6 transition-all duration-300 group"
            data-testid={`it-service-card-${index}`}
          >
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
              <service.icon className="w-6 h-6 text-blue-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
            <p className="text-slate-400 mb-4 leading-relaxed">{service.description}</p>
            {service.link === "/assist-pro" ? (
              <p className="text-cyan-400 text-sm font-medium mb-4">Premium Subscription – £40/month</p>
            ) : (
              <p className="text-green-500 text-sm font-medium mb-4">Free Testing & Diagnostics Included</p>
            )}
            {service.link === "/diagnostic" ? (
              <Link 
                to={service.link}
                className="inline-flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                data-testid="run-diagnostic-btn"
              >
                Run Diagnostic →
              </Link>
            ) : service.link === "/printing-services" ? (
              <Link 
                to={service.link}
                className="inline-flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                data-testid="printing-services-btn"
              >
                <Printer className="w-4 h-4" />
                Order Prints →
              </Link>
            ) : service.link === "/remote-support" ? (
              <Link 
                to={service.link}
                className="inline-flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                data-testid="remote-support-btn"
              >
                Get Remote Help →
              </Link>
            ) : service.link === "/assist-pro" ? (
              <Link 
                to={service.link}
                className="inline-flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:opacity-90 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                data-testid="assist-pro-btn"
              >
                <Headphones className="w-4 h-4" />
                Learn More →
              </Link>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I'm interested in ${service.title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                  data-testid={`whatsapp-btn-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
                <Link 
                  to="/dashboard/new-ticket"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                  data-testid={`ticket-btn-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Ticket className="w-4 h-4" />
                  Open a Ticket
                </Link>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Digital & Business Solutions Section
const DigitalServicesSection = () => (
  <section id="digital-solutions" className="py-20 bg-slate-900/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <p className="text-cyan-500 font-semibold tracking-wide uppercase text-sm mb-3">Grow Your Business</p>
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Digital & Business Solutions</h2>
        <p className="text-slate-400 max-w-2xl mx-auto">
          Modern solutions to help your business grow and succeed online.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {digitalServices.map((service, index) => (
          <motion.div
            key={service.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className={`relative bg-slate-900/60 border border-slate-800 rounded-xl p-6 transition-all duration-300 group ${
              service.comingSoon ? 'opacity-75' : 'hover:border-cyan-500/30'
            }`}
            data-testid={`digital-service-card-${index}`}
          >
            {/* Coming Soon Badge */}
            {service.comingSoon && (
              <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                Coming Soon
              </div>
            )}
            
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
              service.comingSoon ? 'bg-slate-700/50' : 'bg-cyan-500/10 group-hover:bg-cyan-500/20'
            }`}>
              <service.icon className={`w-6 h-6 ${service.comingSoon ? 'text-slate-400' : 'text-cyan-500'}`} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
            <p className="text-slate-400 mb-4 leading-relaxed">{service.description}</p>
            
            {/* Coming Soon - Disabled buttons */}
            {service.comingSoon ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <button 
                  disabled
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-700/50 text-slate-500 font-medium py-2.5 px-4 rounded-lg cursor-not-allowed"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </button>
                <button 
                  disabled
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-700/50 text-slate-500 font-medium py-2.5 px-4 rounded-lg cursor-not-allowed"
                >
                  <Clock className="w-4 h-4" />
                  Coming Soon
                </button>
              </div>
            ) : service.link === "/web-development" ? (
              /* Website Building - WhatsApp + View Services */
              <div className="flex flex-col sm:flex-row gap-2">
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I'm interested in Website Building services`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                  data-testid="whatsapp-btn-website-building"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
                <Link 
                  to="/web-development"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                  data-testid="web-development-btn"
                >
                  <Globe className="w-4 h-4" />
                  View Services →
                </Link>
              </div>
            ) : (
              /* Default - WhatsApp + Open a Ticket */
              <div className="flex flex-col sm:flex-row gap-2">
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I'm interested in ${service.title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                  data-testid={`whatsapp-btn-digital-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
                <Link 
                  to="/dashboard/new-ticket"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                  data-testid={`ticket-btn-digital-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Ticket className="w-4 h-4" />
                  Open a Ticket
                </Link>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// About Section
const AboutSection = () => (
  <section id="about" className="py-24 md:py-32 bg-slate-900/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-blue-500 font-semibold tracking-wide uppercase text-sm mb-4">About SmartIT</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Your Local IT Expert
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed mb-6">
            I am here to help keep your tech happy, healthy, and stress-free! Based in Aylesbury, Buckinghamshire, 
            I provide professional IT solutions for homes and businesses.
          </p>
          <p className="text-slate-400 text-lg leading-relaxed mb-8">
            With years of experience in computer repairs, upgrades, and IT support, I offer personalized service 
            with a commitment to quality and reliability.
          </p>
          
          <div className="grid grid-cols-2 gap-6">
            <div className="card p-6">
              <div className="text-3xl font-bold text-blue-500 mb-2">Free</div>
              <p className="text-slate-400">Testing & Diagnostics</p>
            </div>
            <div className="card p-6">
              <div className="text-3xl font-bold text-blue-500 mb-2">24hr</div>
              <p className="text-slate-400">Quick Turnaround</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="aspect-square rounded-2xl overflow-hidden border border-slate-800">
            <img 
              src="https://images.unsplash.com/photo-1642051615070-c8c2c5caf2a5?auto=format&fit=crop&w=1200&q=80"
              alt="Computer repair technician providing IT support services in Aylesbury, Buckinghamshire - laptop repair and PC diagnostics"
              title="SmartIT - Professional computer repair and IT support in Aylesbury"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 glass p-6 rounded-xl max-w-xs">
            <p className="text-white font-semibold mb-1">Need urgent help?</p>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} className="text-green-400 flex items-center gap-2">
              <Phone className="w-4 h-4" /> WhatsApp us now
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-blue-500 font-semibold tracking-wide uppercase text-sm mb-4">Get In Touch</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Fix Your Tech?
            </h2>
            <p className="text-slate-400 text-lg mb-10">
              Contact us today for a free consultation. We are here to help with all your IT needs.
            </p>

            <div className="space-y-6">
              <a href="tel:+447435324947" className="flex items-center gap-4 text-slate-300 hover:text-white transition-colors">
                <div className="p-3 glass rounded-lg">
                  <Phone className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-semibold">+44 743 532 4947</p>
                </div>
              </a>
              
              <a href="mailto:smartit_itsolutions@outlook.com" className="flex items-center gap-4 text-slate-300 hover:text-white transition-colors">
                <div className="p-3 glass rounded-lg">
                  <Mail className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-semibold">smartit_itsolutions@outlook.com</p>
                </div>
              </a>
              
              <div className="flex items-center gap-4 text-slate-300">
                <div className="p-3 glass rounded-lg">
                  <MapPin className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="font-semibold">Aylesbury, Buckinghamshire, UK</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="card" data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
                  <input
                    type="text"
                    required
                    className="input"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <input
                    type="email"
                    required
                    className="input"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Message</label>
                  <textarea
                    required
                    rows={4}
                    className="input resize-none"
                    placeholder="How can we help?"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    data-testid="contact-message"
                  />
                </div>
                <button type="submit" className="btn-primary w-full justify-center" disabled={submitting} data-testid="contact-submit">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send Message <Send className="w-4 h-4" /></>}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// AuthPage component extracted to /pages/AuthPage.jsx

// Dashboard Layout
const DashboardLayout = ({ children, isAdmin = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = isAdmin ? [
    { icon: LayoutDashboard, label: "Overview", path: "/admin" },
    { icon: Ticket, label: "All Tickets", path: "/admin/tickets" },
    { icon: Globe, label: "Projects", path: "/admin/projects" },
    { icon: MessageSquare, label: "Reviews", path: "/admin/reviews" },
  ] : [
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    { icon: Ticket, label: "My Tickets", path: "/dashboard/tickets" },
    { icon: Plus, label: "New Ticket", path: "/dashboard/new-ticket" },
    { icon: Globe, label: "Start Project", path: "/dashboard/new-project" },
    { icon: FileText, label: "My Projects", path: "/dashboard/projects" },
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-800 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-800">
            <Link to="/" className="text-xl font-bold text-white font-['Manrope']">
              Smart<span className="text-blue-500">IT</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-4 px-4">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => { logout(); navigate("/"); }}
              className="flex items-center gap-3 px-4 py-2 text-slate-400 hover:text-white w-full"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-slate-950/80 backdrop-blur-sm border-b border-slate-800 px-4 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button className="lg:hidden text-white" onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle">
              <Menu />
            </button>
            <h1 className="text-lg font-semibold text-white">{isAdmin ? "Admin Dashboard" : "Client Portal"}</h1>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

// Customer Dashboard
const CustomerDashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets`);
      setTickets(res.data);
    } catch (err) {
      toast.error("Failed to load tickets");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const getStatusBadge = (status) => {
    const badges = {
      open: "badge-open",
      in_progress: "badge-in-progress",
      resolved: "badge-resolved",
      closed: "badge-closed"
    };
    return badges[status] || "badge-open";
  };

  const recentTickets = tickets.slice(0, 5);
  const openCount = tickets.filter(t => t.status === "open").length;
  const resolvedCount = tickets.filter(t => t.status === "resolved").length;

  return (
    <div className="space-y-8" data-testid="customer-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Ticket className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{tickets.length}</p>
              <p className="text-slate-400">Total Tickets</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-500/10 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{openCount}</p>
              <p className="text-slate-400">Open Tickets</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{resolvedCount}</p>
              <p className="text-slate-400">Resolved</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Recent Tickets</h2>
        <Link to="/dashboard/new-ticket" className="btn-primary text-sm py-2 px-4" data-testid="new-ticket-btn">
          <Plus className="w-4 h-4" /> New Ticket
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : recentTickets.length === 0 ? (
        <div className="card text-center py-12">
          <Ticket className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 mb-4">No tickets yet</p>
          <Link to="/dashboard/new-ticket" className="btn-primary inline-flex">
            Create your first ticket
          </Link>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tickets-table">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left p-4 text-slate-400 font-medium">Subject</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden md:table-cell">Category</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden sm:table-cell">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentTickets.map((ticket) => (
                  <tr key={ticket.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="p-4">
                      <Link to={`/dashboard/tickets/${ticket.id}`} className="text-white hover:text-blue-400">
                        {ticket.subject}
                      </Link>
                    </td>
                    <td className="p-4 text-slate-400 hidden md:table-cell capitalize">{ticket.category}</td>
                    <td className="p-4">
                      <span className={`badge ${getStatusBadge(ticket.status)}`}>
                        {ticket.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="p-4 text-slate-400 hidden sm:table-cell">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// New Ticket Form
const NewTicketForm = () => {
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    category: "repair",
    priority: "medium",
    request_quote: false
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/tickets`, formData);
      toast.success("Ticket created successfully!");
      navigate("/dashboard/tickets");
    } catch (err) {
      toast.error("Failed to create ticket");
    }
    setLoading(false);
  };

  return (
    <div className="max-w-2xl" data-testid="new-ticket-form">
      <h2 className="text-2xl font-bold text-white mb-6">Create Support Ticket</h2>
      
      {/* Free Diagnostics Banner */}
      <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20" data-testid="free-diagnostics-banner">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-green-500/20 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h3 className="font-semibold text-green-400 mb-1">Free Testing & Diagnostics</h3>
            <p className="text-slate-400 text-sm">All our services include FREE testing and diagnostics. We will assess your device at no cost before any work begins!</p>
          </div>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="card space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Subject</label>
          <input
            type="text"
            required
            className="input"
            placeholder="Brief description of your issue"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            data-testid="ticket-subject"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Category</label>
            <select
              className="input"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              data-testid="ticket-category"
            >
              <option value="repair">Repair</option>
              <option value="upgrade">Upgrade</option>
              <option value="data_recovery">Data Recovery</option>
              <option value="remote_support">Remote Support</option>
              <option value="consulting">Consulting</option>
              <option value="website">Website</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Priority</label>
            <select
              className="input"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              data-testid="ticket-priority"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
          <textarea
            required
            rows={6}
            className="input resize-none"
            placeholder="Please describe your issue in detail..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            data-testid="ticket-description"
          />
        </div>

        {/* Request Quote Option */}
        <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20" data-testid="request-quote-section">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.request_quote}
              onChange={(e) => setFormData({ ...formData, request_quote: e.target.checked })}
              className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
              data-testid="request-quote-checkbox"
            />
            <div>
              <span className="font-semibold text-blue-400">Request a Quote</span>
              <p className="text-slate-400 text-sm mt-1">Check this box if you would like to receive a price estimate before we proceed with any work. Our quotes are free and come with no obligation.</p>
            </div>
          </label>
        </div>

        <div className="flex gap-4">
          <button type="submit" className="btn-primary" disabled={loading} data-testid="ticket-submit">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Submit Ticket <Send className="w-4 h-4" /></>}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// Ticket List
const TicketList = ({ isAdmin = false }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const fetchTickets = useCallback(async () => {
    try {
      const url = isAdmin 
        ? `${API}/admin/tickets${filter ? `?status=${filter}` : ""}`
        : `${API}/tickets`;
      const res = await axios.get(url);
      setTickets(res.data);
    } catch (err) {
      toast.error("Failed to load tickets");
    }
    setLoading(false);
  }, [isAdmin, filter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const getStatusBadge = (status) => {
    const badges = {
      open: "badge-open",
      in_progress: "badge-in-progress",
      resolved: "badge-resolved",
      closed: "badge-closed"
    };
    return badges[status] || "badge-open";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "text-slate-400",
      medium: "text-blue-400",
      high: "text-yellow-400",
      urgent: "text-red-400"
    };
    return colors[priority] || "text-slate-400";
  };

  return (
    <div data-testid="ticket-list">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-white">{isAdmin ? "All Tickets" : "My Tickets"}</h2>
        <div className="flex gap-4">
          {isAdmin && (
            <select
              className="input w-auto"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          )}
          {!isAdmin && (
            <Link to="/dashboard/new-ticket" className="btn-primary text-sm py-2 px-4">
              <Plus className="w-4 h-4" /> New Ticket
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="card text-center py-12">
          <Ticket className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No tickets found</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left p-4 text-slate-400 font-medium">Subject</th>
                  {isAdmin && <th className="text-left p-4 text-slate-400 font-medium hidden lg:table-cell">Customer</th>}
                  <th className="text-left p-4 text-slate-400 font-medium hidden md:table-cell">Category</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden sm:table-cell">Priority</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden sm:table-cell">Date</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="p-4">
                      <Link 
                        to={isAdmin ? `/admin/tickets/${ticket.id}` : `/dashboard/tickets/${ticket.id}`} 
                        className="text-white hover:text-blue-400"
                      >
                        {ticket.subject}
                      </Link>
                    </td>
                    {isAdmin && (
                      <td className="p-4 text-slate-400 hidden lg:table-cell">
                        {ticket.user_name}<br />
                        <span className="text-sm text-slate-500">{ticket.user_email}</span>
                      </td>
                    )}
                    <td className="p-4 text-slate-400 hidden md:table-cell capitalize">{ticket.category.replace("_", " ")}</td>
                    <td className={`p-4 capitalize hidden sm:table-cell ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`badge ${getStatusBadge(ticket.status)}`}>
                          {ticket.status.replace("_", " ")}
                        </span>
                        {ticket.request_quote && (
                          <span className="badge bg-purple-500/10 text-purple-400 border border-purple-500/20 text-xs">
                            Quote
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-slate-400 hidden sm:table-cell">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== PROJECT PORTAL COMPONENTS ==============

// New Project Form (Client Portal)
const NewProjectForm = () => {
  const [serviceType, setServiceType] = useState("");
  const [formData, setFormData] = useState({});
  const [products, setProducts] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentProductId, setCurrentProductId] = useState(null);
  const [currentVariantId, setCurrentVariantId] = useState(null);
  const navigate = useNavigate();

  const serviceOptions = [
    { value: "1-3-page", label: "1–3 Page Website", price: "£150" },
    { value: "full-business", label: "Full Business Website", price: "£300" },
    { value: "ecommerce", label: "Custom E-commerce Website", price: "£600" },
    { value: "mini-app", label: "Browser-Based Mini-App", price: "£200" },
    { value: "custom", label: "Custom Project", price: "Custom" }
  ];

  const styleOptions = [
    "Modern", "Minimal", "Corporate", "Playful", "Elegant", "Bold"
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Add product (for e-commerce)
  const addProduct = () => {
    const newProduct = {
      id: `product-${Date.now()}`,
      name: "",
      description: "",
      category: "",
      price: "",
      variants: [],
      images: []
    };
    setProducts([...products, newProduct]);
  };

  const updateProduct = (productId, field, value) => {
    setProducts(products.map(p => 
      p.id === productId ? { ...p, [field]: value } : p
    ));
  };

  const removeProduct = (productId) => {
    setProducts(products.filter(p => p.id !== productId));
    setUploadedFiles(uploadedFiles.filter(f => f.productId !== productId));
  };

  // Add variant to product
  const addVariant = (productId) => {
    const newVariant = {
      id: `variant-${Date.now()}`,
      name: "",
      description: "",
      images: []
    };
    setProducts(products.map(p => 
      p.id === productId 
        ? { ...p, variants: [...p.variants, newVariant] } 
        : p
    ));
  };

  const updateVariant = (productId, variantId, field, value) => {
    setProducts(products.map(p => {
      if (p.id !== productId) return p;
      return {
        ...p,
        variants: p.variants.map(v => 
          v.id === variantId ? { ...v, [field]: value } : v
        )
      };
    }));
  };

  const removeVariant = (productId, variantId) => {
    setProducts(products.map(p => {
      if (p.id !== productId) return p;
      return {
        ...p,
        variants: p.variants.filter(v => v.id !== variantId)
      };
    }));
    setUploadedFiles(uploadedFiles.filter(f => f.variantId !== variantId));
  };

  // File upload handler
  const handleFileUpload = async (e, category, productId = null, variantId = null) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    
    for (const file of files) {
      const fileInfo = {
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        file: file,
        category,
        productId,
        variantId,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      };
      setUploadedFiles(prev => [...prev, fileInfo]);
    }
    
    setUploading(false);
    e.target.value = ''; // Reset input
  };

  const removeFile = (fileId) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
  };

  // Submit project
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!serviceType) {
      toast.error("Please select a service type");
      return;
    }

    setSubmitting(true);
    
    try {
      // First create the project
      const projectData = {
        service_type: serviceType,
        ...formData,
        products: products.length > 0 ? products : null
      };

      const res = await axios.post(`${API}/projects`, projectData);

      const projectId = res.data.id;

      // Then upload all files
      for (const fileInfo of uploadedFiles) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', fileInfo.file);
        formDataUpload.append('category', fileInfo.category);
        if (fileInfo.productId) formDataUpload.append('product_id', fileInfo.productId);
        if (fileInfo.variantId) formDataUpload.append('variant_id', fileInfo.variantId);

        await axios.post(`${API}/projects/${projectId}/upload`, formDataUpload, {
          headers: { 
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      toast.success("Project submitted successfully!");
      navigate("/dashboard/projects");
      
    } catch (err) {
      toast.error("Failed to submit project. Please try again.");
    }
    
    setSubmitting(false);
  };

  // Get files by category
  const getFilesByCategory = (category, productId = null, variantId = null) => {
    return uploadedFiles.filter(f => {
      if (f.category !== category) return false;
      if (productId && f.productId !== productId) return false;
      if (variantId && f.variantId !== variantId) return false;
      return true;
    });
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="new-project-form">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Start a New Project</h1>
        <p className="text-slate-400">Fill out the form below to submit your project requirements</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Service Selection */}
        <div className="card">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-400" />
            Select Service
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {serviceOptions.map((option) => (
              <label
                key={option.value}
                className={`card p-4 cursor-pointer border-2 transition-all ${
                  serviceType === option.value
                    ? "border-blue-500 bg-blue-500/10"
                    : "border-slate-700 hover:border-slate-600"
                }`}
              >
                <input
                  type="radio"
                  name="serviceType"
                  value={option.value}
                  checked={serviceType === option.value}
                  onChange={(e) => setServiceType(e.target.value)}
                  className="hidden"
                />
                <p className="text-white font-semibold">{option.label}</p>
                <p className="text-blue-400 text-sm mt-1">From {option.price}</p>
              </label>
            ))}
          </div>
        </div>

        {/* Dynamic Form Based on Service Type */}
        {serviceType && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Website Fields (1-3 page, full business) */}
            {(serviceType === "1-3-page" || serviceType === "full-business") && (
              <>
                <div className="card">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-400" />
                    Business Information
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Business Name *</label>
                      <input
                        type="text"
                        className="input"
                        value={formData.business_name || ""}
                        onChange={(e) => handleInputChange("business_name", e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">About Your Business *</label>
                      <textarea
                        className="input resize-none"
                        rows={4}
                        value={formData.about_business || ""}
                        onChange={(e) => handleInputChange("about_business", e.target.value)}
                        placeholder="Describe your business, what you do, and your target audience..."
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Services/Products List</label>
                      <textarea
                        className="input resize-none"
                        rows={3}
                        value={formData.services_list || ""}
                        onChange={(e) => handleInputChange("services_list", e.target.value)}
                        placeholder="List your main services or products..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Contact Details</label>
                      <textarea
                        className="input resize-none"
                        rows={3}
                        value={formData.contact_details || ""}
                        onChange={(e) => handleInputChange("contact_details", e.target.value)}
                        placeholder="Phone, email, address, social media links..."
                      />
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-purple-400" />
                    Design Preferences
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Preferred Colours</label>
                      <input
                        type="text"
                        className="input"
                        value={formData.preferred_colours || ""}
                        onChange={(e) => handleInputChange("preferred_colours", e.target.value)}
                        placeholder="e.g., Blue and white, #3B82F6..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Style Preference</label>
                      <div className="flex flex-wrap gap-2">
                        {styleOptions.map((style) => (
                          <button
                            key={style}
                            type="button"
                            onClick={() => handleInputChange("style_preference", style)}
                            className={`px-4 py-2 rounded-lg border transition-all ${
                              formData.style_preference === style
                                ? "border-purple-500 bg-purple-500/20 text-purple-400"
                                : "border-slate-700 text-slate-400 hover:border-slate-600"
                            }`}
                          >
                            {style}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* File Uploads for Website */}
                <div className="card">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5 text-green-400" />
                    Upload Files
                  </h2>
                  
                  {/* Logo Upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Logo</label>
                    <div className="flex flex-wrap gap-4 items-center">
                      <label className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, "logo")}
                        />
                        <span className="text-slate-300">Choose Logo</span>
                      </label>
                      {getFilesByCategory("logo").map(f => (
                        <div key={f.id} className="relative">
                          {f.preview ? (
                            <img src={f.preview} alt="Logo" className="w-16 h-16 object-cover rounded-lg" />
                          ) : (
                            <div className="w-16 h-16 bg-slate-800 rounded-lg flex items-center justify-center">
                              <FileText className="w-6 h-6 text-slate-500" />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeFile(f.id)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Gallery Images */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Gallery Images (Team, Products, Location)</label>
                    <div className="flex flex-wrap gap-4 items-start">
                      <label className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          multiple
                          onChange={(e) => handleFileUpload(e, "gallery")}
                        />
                        <span className="text-slate-300">Add Images</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {getFilesByCategory("gallery").map(f => (
                          <div key={f.id} className="relative">
                            {f.preview ? (
                              <img src={f.preview} alt={f.name} className="w-20 h-20 object-cover rounded-lg" />
                            ) : (
                              <div className="w-20 h-20 bg-slate-800 rounded-lg flex items-center justify-center">
                                <FileText className="w-6 h-6 text-slate-500" />
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => removeFile(f.id)}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* E-commerce Fields */}
            {serviceType === "ecommerce" && (
              <>
                <div className="card">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-400" />
                    Business Information
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Business Name *</label>
                      <input
                        type="text"
                        className="input"
                        value={formData.business_name || ""}
                        onChange={(e) => handleInputChange("business_name", e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">About Your Business *</label>
                      <textarea
                        className="input resize-none"
                        rows={3}
                        value={formData.about_business || ""}
                        onChange={(e) => handleInputChange("about_business", e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Product Categories</label>
                      <textarea
                        className="input resize-none"
                        rows={2}
                        value={formData.product_categories || ""}
                        onChange={(e) => handleInputChange("product_categories", e.target.value)}
                        placeholder="e.g., Clothing, Accessories, Electronics..."
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Payment Methods</label>
                        <input
                          type="text"
                          className="input"
                          value={formData.payment_methods || ""}
                          onChange={(e) => handleInputChange("payment_methods", e.target.value)}
                          placeholder="e.g., Card, PayPal, Bank Transfer..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Shipping Rules</label>
                        <input
                          type="text"
                          className="input"
                          value={formData.shipping_rules || ""}
                          onChange={(e) => handleInputChange("shipping_rules", e.target.value)}
                          placeholder="e.g., Free over £50, UK only..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Logo Upload */}
                <div className="card">
                  <h2 className="text-xl font-bold text-white mb-4">Upload Logo</h2>
                  <div className="flex flex-wrap gap-4 items-center">
                    <label className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700">
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, "logo")}
                      />
                      <span className="text-slate-300">Choose Logo</span>
                    </label>
                    {getFilesByCategory("logo").map(f => (
                      <div key={f.id} className="relative">
                        {f.preview && <img src={f.preview} alt="Logo" className="w-16 h-16 object-cover rounded-lg" />}
                        <button type="button" onClick={() => removeFile(f.id)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-white text-xs">×</button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Products Section */}
                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                      <HardDrive className="w-5 h-5 text-amber-400" />
                      Products ({products.length})
                    </h2>
                    <button
                      type="button"
                      onClick={addProduct}
                      className="btn-primary text-sm py-2 px-4"
                    >
                      <Plus className="w-4 h-4" /> Add Product
                    </button>
                  </div>

                  {products.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No products added yet. Click &quot;Add Product&quot; to start.</p>
                  ) : (
                    <div className="space-y-6">
                      {products.map((product, pIdx) => (
                        <div key={product.id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-white font-semibold">Product #{pIdx + 1}</h3>
                            <button
                              type="button"
                              onClick={() => removeProduct(product.id)}
                              className="text-red-400 hover:text-red-300 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <input
                              type="text"
                              className="input"
                              placeholder="Product Name"
                              value={product.name}
                              onChange={(e) => updateProduct(product.id, "name", e.target.value)}
                            />
                            <input
                              type="text"
                              className="input"
                              placeholder="Price (e.g., £29.99)"
                              value={product.price}
                              onChange={(e) => updateProduct(product.id, "price", e.target.value)}
                            />
                          </div>
                          <textarea
                            className="input resize-none mb-4"
                            rows={2}
                            placeholder="Product Description"
                            value={product.description}
                            onChange={(e) => updateProduct(product.id, "description", e.target.value)}
                          />

                          {/* Product Images */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-slate-300 mb-2">Product Images</label>
                            <div className="flex flex-wrap gap-2 items-center">
                              <label className="px-3 py-1 bg-slate-700 border border-slate-600 rounded cursor-pointer hover:bg-slate-600 text-sm">
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/*"
                                  multiple
                                  onChange={(e) => handleFileUpload(e, "product", product.id)}
                                />
                                <span className="text-slate-300">Add Images</span>
                              </label>
                              {getFilesByCategory("product", product.id).map(f => (
                                <div key={f.id} className="relative">
                                  {f.preview && <img src={f.preview} alt={f.name} className="w-12 h-12 object-cover rounded" />}
                                  <button type="button" onClick={() => removeFile(f.id)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs">×</button>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Variants */}
                          <div className="border-t border-slate-700 pt-4 mt-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-slate-300 text-sm font-medium">Variants (Size, Colour, etc.)</span>
                              <button
                                type="button"
                                onClick={() => addVariant(product.id)}
                                className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                              >
                                <Plus className="w-3 h-3" /> Add Variant
                              </button>
                            </div>
                            
                            {product.variants.map((variant, vIdx) => (
                              <div key={variant.id} className="bg-slate-900/50 rounded-lg p-3 mb-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-slate-400 text-xs">Variant #{vIdx + 1}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeVariant(product.id, variant.id)}
                                    className="text-red-400 hover:text-red-300 text-xs"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mb-2">
                                  <input
                                    type="text"
                                    className="input text-sm py-2"
                                    placeholder="Variant Name (e.g., Red, Large)"
                                    value={variant.name}
                                    onChange={(e) => updateVariant(product.id, variant.id, "name", e.target.value)}
                                  />
                                  <input
                                    type="text"
                                    className="input text-sm py-2"
                                    placeholder="Description"
                                    value={variant.description}
                                    onChange={(e) => updateVariant(product.id, variant.id, "description", e.target.value)}
                                  />
                                </div>
                                
                                {/* Variant Images */}
                                <div className="flex flex-wrap gap-2 items-center">
                                  <label className="px-2 py-1 bg-slate-800 border border-slate-700 rounded cursor-pointer hover:bg-slate-700 text-xs">
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept="image/*"
                                      multiple
                                      onChange={(e) => handleFileUpload(e, "variant", product.id, variant.id)}
                                    />
                                    <span className="text-slate-400">Add Images</span>
                                  </label>
                                  {getFilesByCategory("variant", product.id, variant.id).map(f => (
                                    <div key={f.id} className="relative">
                                      {f.preview && <img src={f.preview} alt={f.name} className="w-10 h-10 object-cover rounded" />}
                                      <button type="button" onClick={() => removeFile(f.id)} className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-white text-xs">×</button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Mini-App Fields */}
            {serviceType === "mini-app" && (
              <div className="card">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-green-400" />
                  Mini-App Requirements
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">What problem should this app solve? *</label>
                    <textarea
                      className="input resize-none"
                      rows={4}
                      value={formData.problem_to_solve || ""}
                      onChange={(e) => handleInputChange("problem_to_solve", e.target.value)}
                      placeholder="Describe the main problem or task this app should handle..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Required Fields/Inputs</label>
                    <textarea
                      className="input resize-none"
                      rows={3}
                      value={formData.required_fields || ""}
                      onChange={(e) => handleInputChange("required_fields", e.target.value)}
                      placeholder="e.g., Name, Email, Date, File Upload, Dropdown selections..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Required Automations</label>
                    <textarea
                      className="input resize-none"
                      rows={3}
                      value={formData.required_automations || ""}
                      onChange={(e) => handleInputChange("required_automations", e.target.value)}
                      placeholder="e.g., Send email on submit, calculate totals, generate PDF..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">User Roles</label>
                    <input
                      type="text"
                      className="input"
                      value={formData.user_roles || ""}
                      onChange={(e) => handleInputChange("user_roles", e.target.value)}
                      placeholder="e.g., Admin, Staff, Customer..."
                    />
                  </div>
                  
                  {/* Example Files */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Upload Examples or Screenshots</label>
                    <div className="flex flex-wrap gap-4 items-start">
                      <label className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700">
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*,.pdf,.doc,.docx"
                          multiple
                          onChange={(e) => handleFileUpload(e, "example")}
                        />
                        <span className="text-slate-300">Add Files</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {getFilesByCategory("example").map(f => (
                          <div key={f.id} className="relative bg-slate-800 rounded-lg p-2 flex items-center gap-2">
                            {f.preview ? (
                              <img src={f.preview} alt={f.name} className="w-10 h-10 object-cover rounded" />
                            ) : (
                              <FileText className="w-6 h-6 text-slate-500" />
                            )}
                            <span className="text-xs text-slate-400 max-w-[100px] truncate">{f.name}</span>
                            <button type="button" onClick={() => removeFile(f.id)} className="text-red-400 hover:text-red-300">×</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Custom Project Fields */}
            {serviceType === "custom" && (
              <div className="card">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-400" />
                  Custom Project Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Business/Project Name</label>
                    <input
                      type="text"
                      className="input"
                      value={formData.business_name || ""}
                      onChange={(e) => handleInputChange("business_name", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Project Description *</label>
                    <textarea
                      className="input resize-none"
                      rows={6}
                      value={formData.project_description || ""}
                      onChange={(e) => handleInputChange("project_description", e.target.value)}
                      placeholder="Describe your project in detail. What do you want to build? What features do you need?"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Contact Details</label>
                    <textarea
                      className="input resize-none"
                      rows={2}
                      value={formData.contact_details || ""}
                      onChange={(e) => handleInputChange("contact_details", e.target.value)}
                      placeholder="How can we reach you?"
                    />
                  </div>
                  
                  {/* Document Upload */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Upload Reference Files</label>
                    <div className="flex flex-wrap gap-4 items-start">
                      <label className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700">
                        <input
                          type="file"
                          className="hidden"
                          multiple
                          onChange={(e) => handleFileUpload(e, "document")}
                        />
                        <span className="text-slate-300">Add Files</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {getFilesByCategory("document").map(f => (
                          <div key={f.id} className="relative bg-slate-800 rounded-lg p-2 flex items-center gap-2">
                            {f.preview ? (
                              <img src={f.preview} alt={f.name} className="w-10 h-10 object-cover rounded" />
                            ) : (
                              <FileText className="w-6 h-6 text-slate-500" />
                            )}
                            <span className="text-xs text-slate-400 max-w-[100px] truncate">{f.name}</span>
                            <button type="button" onClick={() => removeFile(f.id)} className="text-red-400 hover:text-red-300">×</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            <div className="card">
              <h2 className="text-lg font-bold text-white mb-4">Additional Notes</h2>
              <textarea
                className="input resize-none"
                rows={3}
                value={formData.additional_notes || ""}
                onChange={(e) => handleInputChange("additional_notes", e.target.value)}
                placeholder="Any other information you'd like to share..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="px-6 py-3 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !serviceType}
                className="btn-primary px-8 py-3"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Project
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </form>
    </div>
  );
};

// Project List (Customer View)
const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (err) {
      toast.error("Failed to load projects");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const getStatusBadge = (status) => {
    const badges = {
      new: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      in_progress: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      waiting_info: "bg-orange-500/10 text-orange-400 border-orange-500/20",
      completed: "bg-green-500/10 text-green-400 border-green-500/20"
    };
    return badges[status] || badges.new;
  };

  return (
    <div data-testid="project-list">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">My Projects</h2>
        <Link to="/dashboard/new-project" className="btn-primary text-sm py-2 px-4">
          <Plus className="w-4 h-4" /> New Project
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : projects.length === 0 ? (
        <div className="card text-center py-12">
          <Globe className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 mb-4">No projects yet</p>
          <Link to="/dashboard/new-project" className="btn-primary inline-flex">
            Start your first project
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {projects.map((project) => (
            <div key={project.id} className="card">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{project.service_name}</h3>
                    <span className={`badge border ${getStatusBadge(project.status)}`}>
                      {project.status.replace("_", " ")}
                    </span>
                  </div>
                  {project.business_name && (
                    <p className="text-slate-400">{project.business_name}</p>
                  )}
                  <p className="text-slate-500 text-sm mt-1">
                    Submitted: {new Date(project.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 text-sm">
                    {project.uploaded_files?.length || 0} files
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Projects View
const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/projects`);
      let data = res.data;
      if (filter) {
        data = data.filter(p => p.status === filter);
      }
      setProjects(data);
    } catch (err) {
      toast.error("Failed to load projects");
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const updateProjectStatus = async (projectId, status, notes = null) => {
    try {
      const updateData = { status };
      if (notes !== null) updateData.admin_notes = notes;
      
      await axios.put(`${API}/admin/projects/${projectId}`, updateData);
      toast.success("Project updated");
      fetchProjects();
      if (selectedProject?.id === projectId) {
        setSelectedProject({ ...selectedProject, status, admin_notes: notes || selectedProject.admin_notes });
      }
    } catch (err) {
      toast.error("Failed to update project");
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/projects/${projectToDelete.id}`);
      toast.success("Project deleted successfully");
      fetchProjects();
      setSelectedProject(null);
    } catch (err) {
      toast.error("Failed to delete project");
    }
    setDeleting(false);
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const openDeleteModal = (project) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      in_progress: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      waiting_info: "bg-orange-500/10 text-orange-400 border-orange-500/20",
      completed: "bg-green-500/10 text-green-400 border-green-500/20"
    };
    return badges[status] || badges.new;
  };

  return (
    <div data-testid="admin-projects">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Delete Project?</h3>
              <p className="text-slate-400 mb-2">
                <span className="text-white font-semibold">{projectToDelete?.service_type?.replace('_', ' ')}</span>
              </p>
              <p className="text-slate-400 mb-6">This action cannot be undone. The project and all its files will be permanently deleted.</p>
              <div className="flex gap-3">
                <button 
                  onClick={() => { setShowDeleteModal(false); setProjectToDelete(null); }}
                  className="flex-1 py-2.5 px-4 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleDeleteProject}
                  disabled={deleting}
                  className="flex-1 py-2.5 px-4 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Trash2 className="w-4 h-4" /> Delete</>}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-white">All Projects</h2>
        <select
          className="input w-auto"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="in_progress">In Progress</option>
          <option value="waiting_info">Waiting for Info</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : projects.length === 0 ? (
        <div className="card text-center py-12">
          <Globe className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No projects found</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Projects List */}
          <div className="space-y-4">
            {projects.map((project) => (
              <div
                key={project.id}
                onClick={() => setSelectedProject(project)}
                className={`card cursor-pointer transition-all ${
                  selectedProject?.id === project.id ? "border-blue-500" : "hover:border-slate-700"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`badge border ${getStatusBadge(project.status)}`}>
                        {project.status.replace("_", " ")}
                      </span>
                      <span className="text-slate-500 text-sm">{project.service_name}</span>
                    </div>
                    <h3 className="text-white font-semibold">{project.business_name || "Unnamed Project"}</h3>
                    <p className="text-slate-400 text-sm">{project.user_name} ({project.user_email})</p>
                    <p className="text-slate-500 text-xs mt-1">
                      {new Date(project.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-slate-500 text-sm">{project.uploaded_files?.length || 0} files</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Project Detail */}
          {selectedProject && (
            <div className="card sticky top-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Project Details</h3>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Status Update */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                <select
                  className="input"
                  value={selectedProject.status}
                  onChange={(e) => updateProjectStatus(selectedProject.id, e.target.value)}
                >
                  <option value="new">New</option>
                  <option value="in_progress">In Progress</option>
                  <option value="waiting_info">Waiting for Info</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              {/* Customer Info */}
              <div className="mb-6 p-4 bg-slate-800/50 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Customer</h4>
                <p className="text-slate-300">{selectedProject.user_name}</p>
                <p className="text-slate-400 text-sm">{selectedProject.user_email}</p>
              </div>

              {/* Service & Business Info */}
              <div className="mb-6">
                <h4 className="text-white font-semibold mb-2">Service Type</h4>
                <p className="text-blue-400">{selectedProject.service_name}</p>
              </div>

              {selectedProject.business_name && (
                <div className="mb-4">
                  <h4 className="text-slate-400 text-sm">Business Name</h4>
                  <p className="text-white">{selectedProject.business_name}</p>
                </div>
              )}

              {selectedProject.about_business && (
                <div className="mb-4">
                  <h4 className="text-slate-400 text-sm">About Business</h4>
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{selectedProject.about_business}</p>
                </div>
              )}

              {selectedProject.services_list && (
                <div className="mb-4">
                  <h4 className="text-slate-400 text-sm">Services/Products</h4>
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{selectedProject.services_list}</p>
                </div>
              )}

              {selectedProject.problem_to_solve && (
                <div className="mb-4">
                  <h4 className="text-slate-400 text-sm">Problem to Solve</h4>
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{selectedProject.problem_to_solve}</p>
                </div>
              )}

              {selectedProject.project_description && (
                <div className="mb-4">
                  <h4 className="text-slate-400 text-sm">Project Description</h4>
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{selectedProject.project_description}</p>
                </div>
              )}

              {/* Products (for e-commerce) */}
              {selectedProject.products?.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-white font-semibold mb-3">Products ({selectedProject.products.length})</h4>
                  <div className="space-y-3">
                    {selectedProject.products.map((product, idx) => (
                      <div key={product.id} className="bg-slate-800/50 rounded-lg p-3">
                        <p className="text-white font-medium">{product.name || `Product ${idx + 1}`}</p>
                        {product.price && <p className="text-blue-400 text-sm">{product.price}</p>}
                        {product.description && <p className="text-slate-400 text-sm mt-1">{product.description}</p>}
                        {product.variants?.length > 0 && (
                          <div className="mt-2 pl-3 border-l border-slate-700">
                            <p className="text-slate-500 text-xs mb-1">Variants:</p>
                            {product.variants.map((v, vi) => (
                              <p key={v.id} className="text-slate-300 text-sm">• {v.name}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Uploaded Files */}
              {selectedProject.uploaded_files?.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-white font-semibold mb-3">Uploaded Files ({selectedProject.uploaded_files.length})</h4>
                  <div className="space-y-2">
                    {selectedProject.uploaded_files.map((file) => (
                      <div key={file.id} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <div>
                            <p className="text-slate-300 text-sm">{file.original_name}</p>
                            <p className="text-slate-500 text-xs">{file.category}</p>
                          </div>
                        </div>
                        <a
                          href={`${API}/projects/${selectedProject.id}/files/${file.id}`}
                          className="text-blue-400 hover:text-blue-300 text-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">Admin Notes</label>
                <textarea
                  className="input resize-none"
                  rows={3}
                  value={selectedProject.admin_notes || ""}
                  onChange={(e) => setSelectedProject({ ...selectedProject, admin_notes: e.target.value })}
                  placeholder="Internal notes..."
                />
                <button
                  onClick={() => updateProjectStatus(selectedProject.id, selectedProject.status, selectedProject.admin_notes)}
                  className="mt-2 text-sm text-blue-400 hover:text-blue-300"
                >
                  Save Notes
                </button>
              </div>

              {/* Delete Project Button */}
              <div className="pt-4 border-t border-slate-700">
                <button
                  onClick={() => openDeleteModal(selectedProject)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors"
                  data-testid="delete-project-btn"
                >
                  <Trash2 className="w-4 h-4" /> Delete Project
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Admin Reviews Management
const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [updating, setUpdating] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/reviews`);
      let data = res.data;
      if (filter) {
        data = data.filter(r => r.status === filter);
      }
      setReviews(data);
    } catch (err) {
      toast.error("Failed to load reviews");
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const updateReviewStatus = async (reviewId, newStatus) => {
    setUpdating(reviewId);
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}`, null, {
        params: { status: newStatus }
      });
      toast.success(`Review ${newStatus === 'approved' ? 'approved' : 'rejected'}`);
      fetchReviews();
    } catch (err) {
      toast.error("Failed to update review");
    }
    setUpdating(null);
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`);
      toast.success("Review deleted");
      fetchReviews();
    } catch (err) {
      toast.error("Failed to delete review");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      approved: "bg-green-500/10 text-green-400 border-green-500/20",
      rejected: "bg-red-500/10 text-red-400 border-red-500/20"
    };
    return badges[status] || badges.pending;
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={i < rating ? "text-yellow-400" : "text-slate-600"}>★</span>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-reviews">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-white">Client Reviews</h2>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-slate-800 border border-slate-700 text-white rounded-lg px-4 py-2 text-sm"
          data-testid="reviews-filter"
        >
          <option value="">All Reviews</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No reviews found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5"
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h3 className="text-white font-semibold">{review.name}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${getStatusBadge(review.status)}`}>
                      {review.status}
                    </span>
                    <div className="flex text-sm">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-1">{review.email}</p>
                  <h4 className="text-blue-400 font-medium mb-2">{review.title}</h4>
                  <p className="text-slate-300 text-sm">{review.message}</p>
                  <p className="text-slate-500 text-xs mt-3">
                    Submitted: {new Date(review.created_at).toLocaleDateString('en-GB', { 
                      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {review.status === 'pending' && (
                    <>
                      <button
                        onClick={() => updateReviewStatus(review.id, 'approved')}
                        disabled={updating === review.id}
                        className="px-4 py-2 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg hover:bg-green-500/20 transition-colors text-sm font-medium disabled:opacity-50"
                        data-testid={`approve-review-${review.id}`}
                      >
                        {updating === review.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Approve"}
                      </button>
                      <button
                        onClick={() => updateReviewStatus(review.id, 'rejected')}
                        disabled={updating === review.id}
                        className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors text-sm font-medium disabled:opacity-50"
                        data-testid={`reject-review-${review.id}`}
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {review.status === 'rejected' && (
                    <button
                      onClick={() => updateReviewStatus(review.id, 'approved')}
                      disabled={updating === review.id}
                      className="px-4 py-2 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg hover:bg-green-500/20 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      Approve
                    </button>
                  )}
                  {review.status === 'approved' && (
                    <button
                      onClick={() => updateReviewStatus(review.id, 'rejected')}
                      disabled={updating === review.id}
                      className="px-4 py-2 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-lg hover:bg-yellow-500/20 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      Unpublish
                    </button>
                  )}
                  <button
                    onClick={() => deleteReview(review.id)}
                    className="px-4 py-2 bg-slate-700/50 text-slate-400 border border-slate-600/50 rounded-lg hover:bg-slate-700 hover:text-red-400 transition-colors text-sm font-medium"
                    data-testid={`delete-review-${review.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Ticket Detail
const TicketDetail = ({ isAdmin = false }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [response, setResponse] = useState("");
  const [status, setStatus] = useState("");
  const navigate = useNavigate();
  const ticketId = window.location.pathname.split("/").pop();

  const fetchTicket = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets/${ticketId}`);
      setTicket(res.data);
      setStatus(res.data.status);
      setResponse(res.data.response || "");
      setEditSubject(res.data.subject || "");
      setEditDescription(res.data.description || "");
    } catch (err) {
      toast.error("Failed to load ticket");
    }
    setLoading(false);
  }, [ticketId]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/tickets/${ticketId}`);
      toast.success("Ticket deleted successfully");
      navigate("/admin/tickets");
    } catch (err) {
      toast.error("Failed to delete ticket");
    }
    setDeleting(false);
    setShowDeleteModal(false);
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const res = await axios.put(`${API}/admin/tickets/${ticketId}`, {
        status,
        response
      });
      setTicket(res.data);
      toast.success("Ticket updated successfully");
    } catch (err) {
      toast.error("Failed to update ticket");
    }
    setUpdating(false);
  };

  const getStatusBadge = (s) => {
    const badges = {
      open: "badge-open",
      in_progress: "badge-in-progress",
      resolved: "badge-resolved",
      closed: "badge-closed"
    };
    return badges[s] || "badge-open";
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="card text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-slate-400">Ticket not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ticket-detail">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Delete Ticket?</h3>
              <p className="text-slate-400 mb-6">This action cannot be undone. The ticket and all its data will be permanently deleted.</p>
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 py-2.5 px-4 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 py-2.5 px-4 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Trash2 className="w-4 h-4" /> Delete</>}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Link 
            to={isAdmin ? "/admin/tickets" : "/dashboard/tickets"} 
            className="text-slate-400 hover:text-white"
          >
            &larr; Back
          </Link>
          <span className={`badge ${getStatusBadge(ticket.status)}`}>
            {ticket.status.replace("_", " ")}
          </span>
          {ticket.request_quote && (
            <span className="badge bg-purple-500/10 text-purple-400 border border-purple-500/20" data-testid="quote-badge">
              Quote Requested
            </span>
          )}
        </div>
        
        {isAdmin && (
          <button 
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 py-2 px-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors"
            data-testid="delete-ticket-btn"
          >
            <Trash2 className="w-4 h-4" /> Delete Ticket
          </button>
        )}
      </div>

      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-4">{ticket.subject}</h2>
        
        {ticket.request_quote && (
          <div className="mb-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-purple-400" />
              <span className="font-semibold text-purple-400">Customer has requested a quote</span>
            </div>
            <p className="text-slate-400 text-sm mt-1">Please provide a price estimate in your response.</p>
          </div>
        )}
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <p className="text-sm text-slate-500">Category</p>
            <p className="text-white capitalize">{ticket.category.replace("_", " ")}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Priority</p>
            <p className="text-white capitalize">{ticket.priority}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Created</p>
            <p className="text-white">{new Date(ticket.created_at).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Last Updated</p>
            <p className="text-white">{new Date(ticket.updated_at).toLocaleString()}</p>
          </div>
        </div>

        {isAdmin && (
          <div className="mb-6">
            <p className="text-sm text-slate-500 mb-1">Customer</p>
            <p className="text-white">{ticket.user_name}</p>
            <p className="text-slate-400">{ticket.user_email}</p>
          </div>
        )}

        <div className="border-t border-slate-800 pt-6">
          <h3 className="text-lg font-semibold text-white mb-3">Description</h3>
          <p className="text-slate-300 whitespace-pre-wrap">{ticket.description}</p>
        </div>

        {ticket.response && (
          <div className="border-t border-slate-800 pt-6 mt-6">
            <h3 className="text-lg font-semibold text-white mb-3">Response from SmartIT</h3>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <p className="text-slate-300 whitespace-pre-wrap">{ticket.response}</p>
            </div>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Update Ticket</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
              <select
                className="input w-full md:w-auto"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="update-status"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Response to Customer</label>
              <textarea
                rows={4}
                className="input resize-none"
                placeholder="Add your response..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                data-testid="update-response"
              />
            </div>

            <button 
              className="btn-primary" 
              onClick={handleUpdate} 
              disabled={updating}
              data-testid="update-ticket-btn"
            >
              {updating ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update Ticket"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/stats`),
      axios.get(`${API}/admin/tickets`)
    ]).then(([statsRes, ticketsRes]) => {
      setStats(statsRes.data);
      setTickets(ticketsRes.data.slice(0, 5));
    }).catch(() => {
      toast.error("Failed to load data");
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Ticket className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.total_tickets || 0}</p>
              <p className="text-slate-400">Total Tickets</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-500/10 rounded-lg">
              <AlertCircle className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.open_tickets || 0}</p>
              <p className="text-slate-400">Open</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-500/10 rounded-lg">
              <Clock className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.in_progress || 0}</p>
              <p className="text-slate-400">In Progress</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.resolved || 0}</p>
              <p className="text-slate-400">Resolved</p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Recent Tickets</h2>
          <Link to="/admin/tickets" className="text-blue-500 hover:text-blue-400 flex items-center gap-1">
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {tickets.length === 0 ? (
          <div className="card text-center py-8">
            <p className="text-slate-400">No tickets yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <Link
                key={ticket.id}
                to={`/admin/tickets/${ticket.id}`}
                className="card p-4 flex items-center justify-between hover:border-blue-500/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{ticket.subject}</p>
                  <p className="text-sm text-slate-500">{ticket.user_name} • {ticket.category}</p>
                </div>
                <span className={`badge ml-4 ${
                  ticket.status === "open" ? "badge-open" : 
                  ticket.status === "in_progress" ? "badge-in-progress" : 
                  ticket.status === "resolved" ? "badge-resolved" : "badge-closed"
                }`}>
                  {ticket.status.replace("_", " ")}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Footer
const Footer = () => (
  <footer className="py-12 bg-slate-900/50 border-t border-slate-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold text-white font-['Manrope']">
            Smart<span className="text-blue-500">IT</span>
          </span>
          <span className="text-slate-500 text-sm">© 2024</span>
        </div>
        <div className="flex items-center gap-6">
          <a href="https://www.facebook.com/smartitsolution2020" target="_blank" rel="noopener noreferrer" 
            className="text-slate-400 hover:text-blue-500 transition-colors">Facebook</a>
          <a href="mailto:smartit_itsolutions@outlook.com" className="text-slate-400 hover:text-blue-500 transition-colors">Email</a>
          <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer"
            className="text-slate-400 hover:text-green-500 transition-colors">WhatsApp</a>
          <Link to="/privacy-policy" className="text-slate-400 hover:text-blue-500 transition-colors">Privacy Policy</Link>
        </div>
      </div>
      {/* Hidden SEO content - Primary keywords and service areas for search engines and AI */}
      <div className="sr-only" aria-hidden="true">
        <h2>Laptop Repair Aylesbury - SmartIT Professional Computer Services</h2>
        <p>Laptop repair Aylesbury, PC repair Aylesbury, computer repair Aylesbury, IT support Aylesbury, laptop diagnostics Aylesbury, free laptop diagnostic Aylesbury, SSD upgrade Aylesbury, laptop screen replacement Aylesbury, data recovery Aylesbury, virus removal Aylesbury, overheating laptop repair Aylesbury, computer cleaning service Aylesbury, PC running slow fix Aylesbury, home IT support Aylesbury, small business IT support Aylesbury.</p>
        <h3>PC Repair Aylesbury - Areas We Cover</h3>
        <p>Aylesbury, Buckingham, Bicester, Oxford, Milton Keynes, High Wycombe, Tring, Leighton Buzzard, Waddesdon, Haddenham, Thame, Princes Risborough, Wendover, Stoke Mandeville, Aston Clinton, Stone, Wing, Great Missenden, Chinnor, Long Crendon.</p>
        <h3>Computer Repair Services Buckinghamshire</h3>
        <p>SmartIT provides professional laptop repair and PC repair across Buckinghamshire. Free laptop diagnostic available. Expert SSD upgrades, virus removal, data recovery, screen replacement, and overheating repairs. Home IT support and small business IT support in Aylesbury and surrounding towns.</p>
        <nav aria-label="Service links">
          <a href="/diagnostic">Free Laptop Diagnostic Aylesbury</a>
          <a href="/remote-support">Remote IT Support Aylesbury</a>
          <a href="/tutorials">PC Repair Tutorials</a>
          <a href="/about-smartit">About SmartIT Aylesbury</a>
          <a href="/pc-builder">Custom PC Build Aylesbury</a>
        </nav>
      </div>
    </div>
  </footer>
);

// Chatbot Component
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm SmartIT's AI assistant. How can I help you today?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = React.useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I'm having trouble right now. Please contact us at 07435324947 or via WhatsApp." 
      }]);
    }
    setLoading(false);
  };

  return (
    <div className="chatbot-container" data-testid="chatbot">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="chatbot-window"
          >
            <div className="bg-blue-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">SmartIT Support</p>
                  <p className="text-xs text-blue-200">AI Assistant</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.role === "user" 
                      ? "bg-blue-600 text-white" 
                      : "bg-slate-800 text-slate-200"
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-800 rounded-2xl px-4 py-2">
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={sendMessage} className="p-4 border-t border-slate-800">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message..."
                  className="input flex-1"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <button 
                  type="submit" 
                  className="btn-primary p-3"
                  disabled={loading || !input.trim()}
                  data-testid="chat-send"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="chatbot-button"
        data-testid="chatbot-toggle"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </div>
  );
};

// WhatsApp Button
const WhatsAppButton = () => (
  <a
    href={`https://wa.me/${WHATSAPP_NUMBER}`}
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float"
    data-testid="whatsapp-btn"
  >
    <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
  </a>
);

// Landing Page
const LandingPage = () => (
  <>
    <SEO {...pageSEO.home} />
    <Navbar />
    <HeroSection />
    <VolunteerCard />
    <ServicesSection />
    <DigitalServicesSection />
    <AboutSection />
    <ContactSection />
    <Footer />
    <WhatsAppButton />
    <Chatbot />
  </>
);

// Main App
function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-right" theme="dark" richColors />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<AuthPage isLogin={true} />} />
            <Route path="/register" element={<AuthPage isLogin={false} />} />
            <Route path="/remote-support" element={<RemoteSupportPage />} />
            <Route path="/promote-your-site" element={<PromoteYourSitePage />} />
            <Route path="/printing-services" element={<PrintingServicesPage />} />
            <Route path="/diagnostic" element={<DiagnosticPage />} />
            <Route path="/web-development" element={<WebDevelopmentPage />} />
            <Route path="/pc-builder" element={<PCBuilderPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} /> {/* FROZEN PAGE */}
            <Route path="/tutorials" element={<TutorialsPage />} />
            <Route path="/about-smartit" element={<AboutSmartITPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/sell-device" element={<SellDevicePage />} />
            <Route path="/assist-pro" element={<AssistProPage />} />
            
            {/* Customer Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout><CustomerDashboard /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/tickets" element={
              <ProtectedRoute>
                <DashboardLayout><TicketList /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/new-ticket" element={
              <ProtectedRoute>
                <DashboardLayout><NewTicketForm /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/tickets/:id" element={
              <ProtectedRoute>
                <DashboardLayout><TicketDetail /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/new-project" element={
              <ProtectedRoute>
                <DashboardLayout><NewProjectForm /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/projects" element={
              <ProtectedRoute>
                <DashboardLayout><ProjectList /></DashboardLayout>
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute adminOnly>
                <DashboardLayout isAdmin><AdminDashboard /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/tickets" element={
              <ProtectedRoute adminOnly>
                <DashboardLayout isAdmin><TicketList isAdmin /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/tickets/:id" element={
              <ProtectedRoute adminOnly>
                <DashboardLayout isAdmin><TicketDetail isAdmin /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/projects" element={
              <ProtectedRoute adminOnly>
                <DashboardLayout isAdmin><AdminProjects /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/reviews" element={
              <ProtectedRoute adminOnly>
                <DashboardLayout isAdmin><AdminReviews /></DashboardLayout>
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
